import React from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, Alert, Divider, Stack, Chip, Avatar
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import swal from 'sweetalert';
import Colors from '../../../components/ui/Colors';
import CustomLoader from '../../../components/CustomLoader';
import { previewTableInjection, commitTableInjection, getCommitJobStatus } from '../../../utilityFunctions/AdminUtil';
import PreviewPanel from './PreviewPanel';
import InjectedTablesList from './InjectedTablesList';

const SNAKE_CASE_RE = /^[a-z][a-z0-9_]*$/;

export default function InjectTablePage(props) {
  const { loginUser } = props;
  const jwtToken = loginUser && loginUser.jwtToken;

  const isAdmin = !!(loginUser && Array.isArray(loginUser.roles) && loginUser.roles.some(r => r.authority === 'ADMIN'));

  const [file, setFile] = React.useState(null);
  const [tableName, setTableName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [tableNameError, setTableNameError] = React.useState('');

  const [previewing, setPreviewing] = React.useState(false);
  const [committing, setCommitting] = React.useState(false);
  const [commitJob, setCommitJob] = React.useState(null);
  const [preview, setPreview] = React.useState(null);

  const [refreshKey, setRefreshKey] = React.useState(0);

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">This page is restricted to admin users.</Alert>
      </Box>
    );
  }

  const validateTableName = (name) => {
    if (!name) return 'Required';
    if (name.length > 64) return 'Must be 64 characters or fewer';
    if (!SNAKE_CASE_RE.test(name)) return 'Use snake_case (lowercase letters, digits, underscores; must start with a letter)';
    return '';
  };

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
  };

  const handleTableNameChange = (e) => {
    const v = e.target.value;
    setTableName(v);
    setTableNameError(validateTableName(v));
  };

  const handleGeneratePreview = async () => {
    const nameErr = validateTableName(tableName);
    setTableNameError(nameErr);
    if (nameErr) return;
    if (!file) {
      swal({ title: "Missing file", text: "Choose a CSV or XLSX file first.", icon: "warning" });
      return;
    }

    setPreviewing(true);
    const result = await previewTableInjection(file, tableName, description, jwtToken);
    setPreviewing(false);

    if (!result.ok) {
      swal({ title: "Preview failed", text: result.error || "Unknown error", icon: "error" });
      return;
    }
    setPreview(result.data);
  };

  const handleCancelPreview = () => {
    setPreview(null);
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Poll the async commit job until it reaches a terminal state. The backend
  // returns 202 immediately, so we drive the progress UI off the job status
  // rather than the request itself (which no longer blocks on warehouse
  // latency). The commit runs server-side regardless of this poller — if the
  // admin leaves, a completion email still arrives.
  const pollCommitJob = async (jobId) => {
    const POLL_MS = 3000;
    const MAX_MS = 90 * 60 * 1000; // safety ceiling — large spreadsheets on a cold warehouse can take a while
    const deadline = Date.now() + MAX_MS;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      await sleep(POLL_MS);
      const res = await getCommitJobStatus(jobId, jwtToken);
      if (!res.ok) {
        // Transient network/poll error — keep trying until the deadline.
        if (Date.now() > deadline) {
          return { status: 'FAILED', error: res.error || 'Lost track of the commit job (timed out polling).' };
        }
        continue;
      }
      const job = res.data;
      setCommitJob(job);
      if (job.status === 'SUCCEEDED' || job.status === 'FAILED') {
        return job;
      }
      if (Date.now() > deadline) {
        return { status: 'FAILED', error: 'Commit is still running after 90 minutes — check the server logs or your email.' };
      }
    }
  };

  const handleConfirmCommit = async (editedMetadata) => {
    if (!preview || !preview.stagingId) return;

    setCommitting(true);
    setCommitJob({ status: 'RUNNING', message: 'Submitting…', rowsInserted: 0, rowsTotal: 0 });
    const result = await commitTableInjection(
      { stagingId: preview.stagingId, editedMetadata },
      jwtToken
    );

    if (!result.ok) {
      setCommitting(false);
      setCommitJob(null);
      swal({ title: "Inject failed", text: result.error || "Unknown error", icon: "error" });
      return;
    }

    setCommitJob(result.data);
    const job = await pollCommitJob(result.data.jobId);
    setCommitting(false);

    if (job.status !== 'SUCCEEDED') {
      swal({ title: "Inject failed", text: job.error || "Commit failed", icon: "error" });
      return;
    }

    swal({
      title: "Table injected",
      text: `${job.fullyQualifiedName} is live. The agentic pipeline can query it immediately.`,
      icon: "success",
    });

    setPreview(null);
    setCommitJob(null);
    setFile(null);
    setTableName('');
    setDescription('');
    setRefreshKey((k) => k + 1);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto', width: '100%', boxSizing: 'border-box' }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Avatar
          variant="rounded"
          sx={{ bgcolor: alpha(Colors.primary, 0.12), color: Colors.primary, width: 48, height: 48 }}
        >
          <StorageRoundedIcon />
        </Avatar>
        <Box>
          <Typography variant="h5" sx={{ color: Colors.primary, fontWeight: 700, lineHeight: 1.2 }}>
            Inject table from spreadsheet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload a CSV or XLSX. The system infers the schema, an LLM enriches the metadata, you review &amp; edit, then commit.
            On commit a Delta table is created and the agentic pipeline can query it without an app restart.
          </Typography>
        </Box>
      </Stack>

      {!preview && (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, md: 3 }, mb: 3, mt: 2, borderRadius: 2, overflow: 'hidden' }}
        >
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Box
                component="label"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  textAlign: 'center',
                  p: { xs: 3, md: 4 },
                  border: '2px dashed',
                  borderColor: file ? Colors.secondary : alpha(Colors.primary, 0.35),
                  borderRadius: 2,
                  bgcolor: file ? alpha(Colors.secondary, 0.06) : alpha(Colors.primary, 0.03),
                  cursor: previewing ? 'default' : 'pointer',
                  transition: 'border-color .2s, background-color .2s',
                  '&:hover': previewing ? {} : {
                    borderColor: Colors.primary,
                    bgcolor: alpha(Colors.primary, 0.06),
                  },
                }}
              >
                <Avatar sx={{ bgcolor: alpha(Colors.primary, 0.12), color: Colors.primary, width: 44, height: 44 }}>
                  <UploadFileIcon />
                </Avatar>
                {file ? (
                  <Chip
                    icon={<InsertDriveFileOutlinedIcon />}
                    label={file.name}
                    onDelete={previewing ? undefined : () => setFile(null)}
                    sx={{ maxWidth: '100%', fontWeight: 600 }}
                  />
                ) : (
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: Colors.primary }}>
                    Choose a CSV or XLSX file
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {file ? 'Click to choose a different file' : 'Click to browse — .csv or .xlsx'}
                </Typography>
                <input
                  type="file"
                  hidden
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFileChange}
                  disabled={previewing}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Table name (snake_case)"
                value={tableName}
                onChange={handleTableNameChange}
                error={!!tableNameError}
                helperText={tableNameError || 'lowercase, digits, underscores; ≤ 64 chars'}
                inputProps={{ maxLength: 64 }}
                disabled={previewing}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Admin description (optional, helps the LLM enrich metadata)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={previewing}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AutoAwesomeRoundedIcon />}
                  onClick={handleGeneratePreview}
                  disabled={previewing || !file || !tableName || !!tableNameError}
                  sx={{ backgroundColor: Colors.primary, '&:hover': { backgroundColor: Colors.primary } }}
                >
                  {previewing ? 'Generating preview…' : 'Generate preview'}
                </Button>
              </Stack>
            </Grid>

            {previewing && (
              <Grid item xs={12}><CustomLoader /></Grid>
            )}
          </Grid>
        </Paper>
      )}

      {preview && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3, mt: 2, borderRadius: 2 }}>
          <PreviewPanel
            preview={preview}
            onCancel={handleCancelPreview}
            onConfirm={handleConfirmCommit}
            committing={committing}
            commitJob={commitJob}
            jwtToken={jwtToken}
          />
        </Paper>
      )}

      <Divider sx={{ my: 3 }} />

      <InjectedTablesList jwtToken={jwtToken} refreshKey={refreshKey} />
    </Box>
  );
}
